/*global google*/
import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import {
  TerraDraw,
  TerraDrawCircleMode,
  TerraDrawLineStringMode,
  TerraDrawMarkerMode,
  TerraDrawPolygonMode,
  TerraDrawRectangleMode
} from 'terra-draw';

import {MapContext} from './map-context-shim.js';

const convertFeature = (feature, options) => {
  const {geometry: {coordinates}} = feature;
  switch (feature.geometry.type) {
  case 'Polygon':
    return new google.maps.Polygon({
      ...options,
      paths: coordinates.map(ring => {
        return ring.map(lngLat => {
          return new google.maps.LatLng(lngLat[1], lngLat[0]);
        });
      })
    });
  case 'Point':
    return new google.maps.Marker({
      ...options,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        strokeColor: options.strokeColor,
        strokeOpacity: options.strokeColor,
        strokeWeight: options.strokeWidth,
        fillColor: options.fillColor,
        fillOpacity: options.fillOpacity,
        scale: options.width
      },
      position: new google.maps.LatLng(coordinates[1], coordinates[0])
    });
  case 'LineString':
    return new google.maps.Polyline({
      ...options,
      path: coordinates.map(lngLat => {
        return new google.maps.LatLng(lngLat[1], lngLat[0]);
      })
    });
  default:
    return null;
  }
};

const TerraDrawReactGoogleMapsDrawingManager = ({
  drawingMode,
  options,
  onCircleComplete,
  onPointComplete,
  onOverlayComplete,
  onPolygonComplete,
  onPolylineComplete,
  onRectangleComplete
}) => {
  const map = useContext(MapContext);
  const [enabledDrawing, setEnabledDrawing] = useState(null);
  const drawing = useMemo(() => {
    const {
      circleOptions = {},
      pointOptions = {},
      polygonOptions = {},
      polylineOptions = {},
      rectangleOptions = {}
    } = options;
    const modes = [];
    if (onCircleComplete) {
      const {
        strokeColor = '#000000',
        strokeOpacity = 1.0,
        strokeWidth = 2,
        fillColor = '#000000',
        fillOpacity = 0.3
      } = circleOptions;
      modes.push(new TerraDrawCircleMode({
        styles: {
          outlineColor: strokeColor,
          outlineOpacity: strokeOpacity,
          outlineWidth: strokeWidth,
          fillColor,
          fillOpacity
        }
      }));
    }
    if (onPointComplete) {
      const {
        strokeColor = '#000000',
        strokeOpacity = 1.0,
        strokeWidth = 2,
        fillColor = '#000000',
        fillOpacity = 0.3,
        width = 2
      } = pointOptions;
      modes.push(new TerraDrawMarkerMode({
        styles: {
          pointOutlineColor: strokeColor,
          pointOutlineOpacity: strokeOpacity,
          pointOutlineWidth: strokeWidth,
          pointColor: fillColor,
          pointOpacity: fillOpacity,
          pointWidth: width
        }
      }));
    }
    if (onPolygonComplete) {
      const {
        strokeColor = '#000000',
        strokeOpacity = 1.0,
        strokeWidth = 2,
        fillColor = '#000000',
        fillOpacity = 0.3
      } = polygonOptions;
      modes.push(new TerraDrawPolygonMode({
        styles: {
          outlineColor: strokeColor,
          outlineOpacity: strokeOpacity,
          outlineWidth: strokeWidth,
          fillColor,
          fillOpacity
        }
      }));
    }
    if (onPolylineComplete) {
      const {
        strokeColor = '#000000',
        strokeOpacity = 1.0,
        strokeWidth = 2,
        fillColor = '#000000',
        fillOpacity = 0.3
      } = polylineOptions;
      modes.push(new TerraDrawLineStringMode({
        styles: {
          outlineColor: strokeColor,
          outlineOpacity: strokeOpacity,
          outlineWidth: strokeWidth,
          fillColor,
          fillOpacity
        }
      }));
    }
    if (onRectangleComplete) {
      const {
        strokeColor = '#000000',
        strokeOpacity = 1.0,
        strokeWidth = 2,
        fillColor = '#000000',
        fillOpacity = 0.3
      } = rectangleOptions;
      modes.push(new TerraDrawRectangleMode({
        styles: {
          outlineColor: strokeColor,
          outlineOpacity: strokeOpacity,
          outlineWidth: strokeWidth,
          fillColor,
          fillOpacity
        }
      }));
    }
    const tDraw = new TerraDraw({
      adapter: new TerraDrawGoogleMapsAdapter({
        lib: google.maps,
        map,
        coordinatePrecision: 9
      }),
      modes
    });
    return tDraw;
  }, [
    map,
    options,
    onCircleComplete,
    onPointComplete,
    onPolygonComplete,
    onPolylineComplete,
    onRectangleComplete
  ]);

  const handleFinish = useCallback((finishedId, context) => {
    const {
      circleOptions = {},
      pointOptions = {},
      polygonOptions = {},
      polylineOptions = {},
      rectangleOptions = {}
    } = options;
    const feature = drawing.getSnapshotFeature(finishedId);
    if (feature.properties.mode === 'circle') {
      const gFeature = convertFeature(feature, circleOptions);
      gFeature.setMap(map);
      drawing.clear();
      onCircleComplete?.(gFeature);
      onOverlayComplete?.({type: 'circle', overlay: gFeature});
    }
    if (feature.properties.mode === 'point') {
      const gFeature = convertFeature(feature, pointOptions);
      gFeature.setMap(map);
      drawing.clear();
      onPointComplete?.(gFeature);
      onOverlayComplete?.({type: 'point', overlay: gFeature});
    }
    if (feature.properties.mode === 'polygon') {
      const gFeature = convertFeature(feature, polygonOptions);
      gFeature.setMap(map);
      drawing.clear();
      onPolygonComplete?.(gFeature);
      onOverlayComplete?.({type: 'polygon', overlay: gFeature});
    }
    if (feature.properties.mode === 'linestring') {
      const gFeature = convertFeature(feature, polylineOptions);
      gFeature.setMap(map);
      drawing.clear();
      onPolylineComplete?.(gFeature);
      onOverlayComplete?.({type: 'linestring', overlay: gFeature});
    }
    if (feature.properties.mode === 'rectangle') {
      const gFeature = convertFeature(feature, rectangleOptions);
      gFeature.setMap(map);
      drawing.clear();
      onRectangleComplete?.(gFeature);
      onOverlayComplete?.({type: 'rectangle', overlay: gFeature});
    }
  }, [
    map,
    options,
    onCircleComplete,
    onPointComplete,
    onPolygonComplete,
    onPolylineComplete,
    onRectangleComplete,
    onOverlayComplete,
    drawing
  ]);

  useEffect(() => {
    if (enabledDrawing) {
      switch (drawingMode) {
      case 'marker':
        enabledDrawing.clear();
        enabledDrawing.setMode('point');
        break;
      case 'polygon':
        enabledDrawing.clear();
        enabledDrawing.setMode('polygon');
        break;
      case 'polyline':
        enabledDrawing.clear();
        enabledDrawing.setMode('linestring');
        break;
      case 'rectangle':
        enabledDrawing.clear();
        enabledDrawing.setMode('rectangle');
        break;
      case 'circle':
        enabledDrawing.clear();
        enabledDrawing.setMode('circle');
        break;
      case null:
      default:
        enabledDrawing.clear();
        enabledDrawing.setMode('static');
        break;
      }
    }
  }, [enabledDrawing, drawingMode]);

  useEffect(() => {
    if (drawing) {
      const ready = () => {
        setEnabledDrawing(drawing);
      };
      drawing.start();
      drawing.setMode('static');
      drawing.clear();
      drawing.on('finish', handleFinish);
      drawing.on('ready', ready);
      return () => {
        setEnabledDrawing(null);
        drawing.off('ready', ready);
        drawing.off('finish', handleFinish);
        drawing.setMode('static');
        drawing.clear();
        drawing.stop();
      };
    }
    return () => {};
  }, [setEnabledDrawing, drawing, handleFinish]);

  return null;
};

TerraDrawReactGoogleMapsDrawingManager.propTypes = {
  drawingMode: PropTypes.any,
  options: PropTypes.any,
  onCircleComplete: PropTypes.func,
  onPointComplete: PropTypes.func,
  onOverlayComplete: PropTypes.func,
  onPolygonComplete: PropTypes.func,
  onPolylineComplete: PropTypes.func,
  onRectangleComplete: PropTypes.func,
};

export default TerraDrawReactGoogleMapsDrawingManager;
